import {
  faCheck, faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { LottieAnimations } from '~/_shared/baseComponents/lottieAnimation';
import { LottieAnimationTypes } from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.types';
import { AnimationPreloadComponent } from '~/_shared/components/topUnderbar/statusItem/animationPreload.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useCreateReportItemsSelector } from '~/store/frontendState/createReport/createReport.selectors';
import { type TopUnderbarItem } from '../item/topUnderbarItem.types';
import { TopUnderbarStatusItemComponent } from './topUnderbarStatus.component';

const exportingAnimationType = LottieAnimationTypes.UploadingImage;
const exportingAnimationProps = {
  segment: LottieAnimations[exportingAnimationType].segments.visor,
  speed: 1,
  size: 36,
  autoplay: true,
  loop: true,
  bothDirections: true,
} as const;

const doneAnimationType = LottieAnimationTypes.CircleDownload;
const doneAnimationProps = {
  segment: LottieAnimations[doneAnimationType].segments.default,
  speed: 1,
  size: 30,
  autoplay: true,
} as const;

const animationsToPreload = [
  {
    type: doneAnimationType,
    ...doneAnimationProps,
  }, {
    type: exportingAnimationType,
    ...exportingAnimationProps,
  },
];

const animationPreload = (
  <AnimationPreloadComponent animationsToPreload={animationsToPreload} />
);

export const useReportExportStatusItems = (): ReadonlyArray<TopUnderbarItem> => {
  const exportReportItems = useCreateReportItemsSelector();
  const theme = useTheme();
  const [t] = useTranslation();

  return useMemo<TopUnderbarItem[]>(() => {
    return exportReportItems.map(item => {
      if (item.status === 'Processing') {
        return {
          key: `export-report-processing-${item.hash}`,
          sticky: true,
          icon: faCircleInfo,
          colors: {
            color: theme.contrasts.selectedAccent2,
            backgroundColor: theme.contrasts.selectedAccent2Background,
          },
          children: (
            <>
              {animationPreload}
              <TopUnderbarStatusItemComponent
                animation={{
                  type: exportingAnimationType,
                  colors: {
                    watermark: theme.iconColors.active,
                    visor: theme.contrasts.selectedAccent2,
                    tick: theme.contrasts.selectedAccent2,
                  },
                  ...exportingAnimationProps,
                }}
                text={{
                  value: `${t('Exporting Report')}...`,
                }}
                suffixText={{
                  value: t('This process can take up to {{toMinutes}} minutes.', { toMinutes: 2 }),
                }}
              />
            </>
          ),
        };
      }

      if (item.status === 'Processed') {
        return {
          key: `export-report-processed-${item.hash}`,
          sticky: true,
          icon: faCheck,
          colors: {
            color: theme.contrasts.success,
            backgroundColor: theme.contrasts.successBackground,
          },
          children: (
            <>
              {animationPreload}
              <TopUnderbarStatusItemComponent
                animation={{
                  type: doneAnimationType,
                  colors: {
                    fillFrom: theme.contrasts.successBackground,
                    borderFrom: theme.iconColors.borderPrimaryHover,
                    downloadIcon: theme.iconColors.borderPrimaryHover,
                    fillTo: theme.iconColors.backgroundSuccessInverted,
                    borderTo: theme.iconColors.backgroundSuccessInvertedHover,
                    tick: theme.contrasts.successBackground,
                  },
                  ...doneAnimationProps,
                }}
                text={{
                  value: t('Download Started'),
                }}
              />
            </>
          ),
        };
      }

      return null;
    }).filter(notNullsy);
  }, [exportReportItems, t, theme.contrasts.selectedAccent2, theme.contrasts.selectedAccent2Background,
    theme.contrasts.success, theme.contrasts.successBackground, theme.iconColors.active,
    theme.iconColors.backgroundSuccessInverted, theme.iconColors.backgroundSuccessInvertedHover,
    theme.iconColors.borderPrimaryHover]);
};
